import React from 'react';

import { Label, Loader } from 'semantic-ui-react';

import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector } from 'react-redux';

import { PageContentNodes } from 'components/modules/content/components';
import { PageContentProvider } from 'components/modules/content/context';
import { useContentSuggestions } from 'components/modules/content/hooks';
import { extractGeneratedPages } from 'components/modules/content/services/page';
import { isAdminUser } from 'libs/auth';
import { selectBusiness, selectBusinessCategory } from 'selectors/business';
import { selectUser } from 'selectors/user';

import { getNonReviewedSuggestionsForPage } from './ContentPage.utils';
import { flagBusinessSaved } from '../../../actions/business';
import API from '../../../libs/api';
import { PageSideNavigation } from '../../modules/pages/components';
import usePages from '../../modules/pages/hooks/use-pages';

import './ContentPage.scss';

export default function ContentPage() {
  const business = useSelector(selectBusiness);
  const hasIntelligentWebsiteProduct = _get(business, 'hasIntelligentWebsiteProduct', false);

  const dispatch = useDispatch();

  const businessCategory = useSelector(selectBusinessCategory);
  const user = useSelector(selectUser);
  const isAdmin = isAdminUser(user);

  const { internalPages, settings, refreshPages } = usePages();
  const { suggestions } = useContentSuggestions();

  const pagesWithGeneratedPages = extractGeneratedPages(internalPages, settings, {
    businessCategory,
  });

  async function onSaveContentNode(contentNode, newContent, { component }) {
    await API.updateContentNodeContent(contentNode.id, newContent, [component.id]);
    await refreshPages();
    dispatch(flagBusinessSaved(true));
  }

  if (_isEmpty(pagesWithGeneratedPages) || _isEmpty(settings)) {
    return <Loader active />;
  }

  const pageContentContextValue = {
    pages: pagesWithGeneratedPages,
    settings,
    refreshPages,
    suggestions,
    onSaveContentNode,
  };

  return (
    <PageContentProvider value={pageContentContextValue}>
      <PageSideNavigation
        className="content-page"
        pages={pagesWithGeneratedPages}
        displayComponent={PageContentNodes}
        renderTitle={({ id, title }) => {
          const suggestionsCount = getNonReviewedSuggestionsForPage(id, suggestions).length;

          const displaySuggestionsCount =
            suggestionsCount > 0 && (isAdmin || hasIntelligentWebsiteProduct);

          return (
            <div>
              <span className="name">{title}</span>
              {displaySuggestionsCount && (
                <span className="suggestions-count">
                  <Label circular color="red" size="tiny">
                    {suggestionsCount}
                  </Label>
                </span>
              )}
            </div>
          );
        }}
      />
    </PageContentProvider>
  );
}
